import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { tag: "", date: "", titre: "", texte: "", image: "" };

export default function AdminNews() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/news`).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, image: form.image || null };
    try {
      if (editing) await axios.put(`${API}/admin/news/${editing}`, payload, { withCredentials: true });
      else await axios.post(`${API}/admin/news`, payload, { withCredentials: true });
      toast.success(editing ? "Article mis à jour" : "Article publié");
      setForm(empty); setEditing(null); setShowForm(false); load();
    } catch {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  const edit = (a) => {
    setForm({ tag: a.tag, date: a.date, titre: a.titre, texte: a.texte, image: a.image || "" });
    setEditing(a.id); setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ?")) return;
    await axios.delete(`${API}/admin/news/${id}`, { withCredentials: true });
    toast.success("Article supprimé"); load();
  };

  return (
    <div data-testid="admin-news">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-[#71717A]">{items.length} article(s) publiés</p>
        <button
          data-testid="news-add-button"
          onClick={() => { setForm(empty); setEditing(null); setShowForm(!showForm); }}
          className="flex items-center gap-2 border border-[#C5A059] text-[#C5A059] px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-[#C5A059] hover:text-black transition-colors duration-300"
        >
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Fermer" : "Nouvel article"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="news-form" className="border border-[#27272A] bg-[#121214] p-8 mb-8 grid sm:grid-cols-2 gap-8">
          <input data-testid="news-input-tag" required placeholder="Tag (ex: Événement) *" value={form.tag} onChange={set("tag")} className="input-editorial" />
          <input data-testid="news-input-date" required placeholder="Date (ex: Novembre 2026) *" value={form.date} onChange={set("date")} className="input-editorial" />
          <input data-testid="news-input-titre" required placeholder="Titre *" value={form.titre} onChange={set("titre")} className="input-editorial sm:col-span-2" />
          <textarea data-testid="news-input-texte" required rows={3} placeholder="Texte *" value={form.texte} onChange={set("texte")} className="input-editorial sm:col-span-2 resize-none" />
          <input data-testid="news-input-image" placeholder="URL image (optionnel, ex: /assets/kinshasa-2026.jpg)" value={form.image} onChange={set("image")} className="input-editorial sm:col-span-2" />
          <div className="sm:col-span-2">
            <button type="submit" data-testid="news-submit-button" className="bg-[#C5A059] text-black px-8 py-3 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors duration-300">
              {editing ? "Mettre à jour" : "Publier"}
            </button>
          </div>
        </form>
      )}

      <div className="border border-[#27272A] divide-y divide-[#27272A]">
        {items.map((a) => (
          <div key={a.id} data-testid={`news-row-${a.id.slice(0, 8)}`} className="flex items-center gap-5 px-5 py-4 hover:bg-[#121214] transition-colors">
            {a.image && <img src={a.image} alt="" className="w-16 h-12 object-cover" />}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{a.titre}</p>
              <p className="text-xs text-[#71717A]"><span className="text-[#C5A059]">{a.tag}</span> · {a.date}</p>
            </div>
            <button onClick={() => edit(a)} data-testid={`news-edit-${a.id.slice(0, 8)}`} className="text-[#71717A] hover:text-[#C5A059] transition-colors" aria-label="Modifier"><Pencil size={15} /></button>
            <button onClick={() => remove(a.id)} data-testid={`news-delete-${a.id.slice(0, 8)}`} className="text-[#71717A] hover:text-red-400 transition-colors" aria-label="Supprimer"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
